import React from "react";
import { H2, HeaderTextBlock, P } from "@/components/Typography";
import {
  faShop,
  faKey,
  faListCheck,
  faIdCard,
  faBagsShopping,
  faMoneyCheckPen,
  faPlay,
} from "@fortawesome/pro-duotone-svg-icons";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { tv } from "tailwind-variants";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  useOnboardingStatus,
  SetupChecklistStep,
} from "@/hooks/useOnboardingStatus";
import { Button } from "@/components/Button";
import { useMatIntegrations } from "@/requests/hooks/mat.ts";
import { COLLECT_HOME_URL } from "@/utils/const.ts";
import Shimmer from "@/components/Shimmer";

const styles = tv({
  slots: {
    checklistContainer: "mt-40 flex flex-col",
    checklistItem: "flex flex-row gap-30",
    checklistColumn: "",
    checklistLineContainer: "flex h-full flex-row justify-center",
    checklistLine: "pointer-events-none block h-full w-[8px] bg-gray-500",
    iconContainer: "flex h-16 w-16 items-center justify-center rounded-md",
    icon: "h-30",
    contentContainer: "max-w-[660px] pb-40",
    completeContainer:
      "mb-50 mt-30 flex h-[88px] max-w-[983px] items-center justify-between overflow-hidden rounded bg-primary-light p-20",
  },
  variants: {
    status: {
      completed: {
        iconContainer: "bg-green",
        icon: "text-white",
        checklistLine: "block w-[2px] bg-green",
      },
      inProgress: {
        iconContainer: "bg-primary",
        icon: "text-white",
        checklistLine: "block w-[2px] bg-primary",
      },
      todo: {
        iconContainer: "border border-primary bg-primary-light",
        icon: "text-primary",
        checklistLine: "block w-[2px] bg-primary/40",
      },
    },
  },
  defaultVariants: {
    status: "inProgress",
  },
});

interface Step {
  id: SetupChecklistStep;
  title: string;
  icon: IconProp;
  description: string;
  isLast?: boolean;
  status?: "completed" | "inProgress" | "todo";
  link: {
    url: string;
    text: string;
  };
}

const getSteps = (integration: string | null): Step[] => [
  {
    id: SetupChecklistStep.INSTALL_DATASHIPS_APP,
    title: t("matOnboarding.steps.installDatashipsApp.title"),
    icon: faShop,
    description: t("matOnboarding.steps.installDatashipsApp.description"),
    link: {
      url: "/integrations/checklist",
      text: t("matOnboarding.steps.installDatashipsApp.link.text"),
    },
  },
  {
    id: SetupChecklistStep.INSTALL_MAT_APP,
    title: t("matOnboarding.steps.installMatApp.title"),
    icon: faMoneyCheckPen,
    description: t("matOnboarding.steps.installMatApp.description"),
    link: {
      url: `/integrations/mat-settings`,
      text: t("matOnboarding.steps.installMatApp.link.text"),
    },
  },
  {
    id: SetupChecklistStep.CONFIGURE_MAT_APP,
    title: t("matOnboarding.steps.configureMatApp.title"),
    icon: faKey,
    description: t("matOnboarding.steps.configureMatApp.description"),
    link: {
      // If they haven't done the previous step we need them to do it.
      url: integration
        ? `/integrations/mat-settings/${integration}/configuration`
        : `/integrations/mat-settings`,
      text: t("matOnboarding.steps.configureMatApp.link.text"),
    },
  },
  {
    id: SetupChecklistStep.CONFIGURE_PRIVACY_DETAILS,
    title: t("matOnboarding.steps.configurePrivacyDetails.title"),
    icon: faListCheck,
    description: t("matOnboarding.steps.configurePrivacyDetails.description"),
    link: {
      url: "/integrations/shop-settings/privacy-details",
      text: t("matOnboarding.steps.configurePrivacyDetails.link.text"),
    },
  },
  {
    id: SetupChecklistStep.CONFIGURE_CHECKOUT,
    title: t("matOnboarding.steps.configureCheckout.title"),
    icon: faIdCard,
    description: t("matOnboarding.steps.configureCheckout.description"),
    link: {
      url: "/integrations/shop-settings/disable-checkout",
      text: t("matOnboarding.steps.configureCheckout.link.text"),
    },
  },
  {
    id: SetupChecklistStep.CONFIGURE_DATASHIPS_APP,
    title: t("matOnboarding.steps.configureDatashipsApp.title"),
    icon: faBagsShopping,
    isLast: true,
    description: t("matOnboarding.steps.configureDatashipsApp.description"),
    link: {
      url: "/integrations/shop-settings/installation",
      text: t("matOnboarding.steps.configureDatashipsApp.link.text"),
    },
  },
];

const renderChecklistItem = (step: Step) => {
  const {
    checklistItem,
    checklistColumn,
    checklistLineContainer,
    checklistLine,
    iconContainer,
    icon,
    contentContainer,
  } = styles({ status: step.status });

  return (
    <div className={checklistItem()} key={step.title}>
      <div className={checklistColumn()}>
        <div className={iconContainer()}>
          <FontAwesomeIcon icon={step.icon} className={icon()} />
        </div>
        <div className={checklistLineContainer()}>
          {!step.isLast && <div className={checklistLine()}></div>}
        </div>
      </div>
      <div className={contentContainer()}>
        <H2 className="font-medium">{step.title}</H2>
        <P>{step.description}</P>

        {step.link && step.status !== "completed" && (
          <Button
            as="link"
            variant="link"
            to={step.link ? step.link.url : "#"}
            extraClassNames=""
            icon={faPlay}
            iconPosition="end"
            iconClassName="h-10">
            {step.link ? step.link.text : "Placeholder"}
          </Button>
        )}
      </div>
    </div>
  );
};

const renderCompleteSetup = () => {
  const { completeContainer } = styles();

  return (
    <div className={completeContainer()}>
      <P>🎉 Congratulations you have completed onboarding!</P>
      <Button variant="primary" as="link" to={COLLECT_HOME_URL}>
        {t("matOnboarding.checklist.completeButton.cta")}
      </Button>
    </div>
  );
};

const OnboardingChecklist = () => {
  const { onboardingStatus, isLoading: onboardingStatusLoading } =
    useOnboardingStatus();

  const { data: matIntegrationsData, isLoading: matIntegrationsLoading } =
    useMatIntegrations();

  const renderLoading = () => (
    <div className="px-25 py-20">
      <Shimmer width="100%" height={1142} />
    </div>
  );

  if (onboardingStatusLoading || matIntegrationsLoading) {
    return renderLoading();
  }

  const getInstalledMatIntegration = (): string | null => {
    const integration = matIntegrationsData?.find(integration => {
      return (
        integration.installationStatus === "INSTALLED" ||
        integration.installationStatus === "PROVISIONING" ||
        integration.installationStatus === "ENABLED"
      );
    })?.integration;

    if (!integration) return null;

    return integration.toLocaleLowerCase();
  };

  // Find the first uncompleted step and mark it as inProgress
  const firstUncompletedStep = onboardingStatus.steps.find(
    step => !step.completed,
  );

  const firstUncompletedId = firstUncompletedStep
    ? firstUncompletedStep.id
    : null;

  const integrationName = getInstalledMatIntegration();

  const stepsWithStatus = getSteps(integrationName).map(step => {
    let status: "completed" | "inProgress" | "todo";
    const onboardingStep = onboardingStatus.steps.find(s => s.id === step.id);

    if (onboardingStep && onboardingStep.completed) {
      status = "completed";
    } else if (onboardingStep && onboardingStep.id === firstUncompletedId) {
      status = "inProgress";
    } else {
      status = "todo";
    }

    return { ...step, status };
  });

  const { checklistContainer } = styles();

  return (
    <>
      <HeaderTextBlock
        align="left"
        featureText={t("matOnboarding.checklist.header.feature")}
        headingText={t("matOnboarding.checklist.header.heading")}
        descriptionTextColor="white"
      />

      {firstUncompletedId === null && renderCompleteSetup()}

      <div className={checklistContainer()}>
        {stepsWithStatus.map(step => (
          <React.Fragment key={step.id}>
            {renderChecklistItem(step)}
          </React.Fragment>
        ))}
      </div>
    </>
  );
};

export default OnboardingChecklist;
